import { Controller } from "@hotwired/stimulus";

// Meant to be replaced by the _tw version if DXN-977 tests are successful
export default class extends Controller {
  static targets = ["link", "thumbnail", "thumbnailHover", "productVariantName", "productVariantFirstWord", "productVariantRestOfName", "form", "lazyAlternates"];

  connect() {
    this.hoverTimeout = null
    this.alternatesMaterialized = false
    this.syncThumbnailQueries()
  }

  // Thumbnail hrefs are rendered without the catalogue's query string, so a
  // visitor clicking one directly would land on the product page with no
  // search/filters and a breadcrumb that can't link back to their results.
  // Rewriting the href (rather than intercepting the click) keeps ctrl-click,
  // middle-click and "open in new tab" working. Done here for the
  // server-rendered thumbnails and again after materializing the lazy ones,
  // which costs nothing in HTML: adding the query server-side would repeat it
  // on every thumbnail of every card.
  syncThumbnailQueries() {
    if (!window.location.search) return

    this.element.querySelectorAll(".product-box-alternate[href]").forEach((thumbnail) => {
      thumbnail.setAttribute("href", this.variantUrlWithPageQuery(thumbnail.getAttribute("href")))
    })
  }

  disconnect() {
    this.stopCarousel();
  }

  // Alternates beyond the visible carousel window are not server-rendered:
  // they arrive as a compact JSON payload (see _product_variant_tw.html.erb)
  // and are materialized into the track on the first interaction with the card.
  materializeAlternates() {
    if (this.alternatesMaterialized) return;
    this.alternatesMaterialized = true;
    if (!this.hasLazyAlternatesTarget) return;

    const script = this.lazyAlternatesTarget;
    let alternates;
    try {
      alternates = JSON.parse(script.textContent);
    } catch {
      return;
    } finally {
      // Removing the payload keeps materialization idempotent even across
      // Turbo cache restores, where the controller instance (and its flag)
      // is recreated on an already materialized DOM.
      script.remove();
    }

    const track = this.element.querySelector('[data-thumb-carousel-target="track"]');
    if (!track) return;

    const fragment = document.createDocumentFragment();
    alternates.forEach((alternate) => fragment.appendChild(this.buildAlternateElement(alternate)));
    track.appendChild(fragment);
    this.syncThumbnailQueries();
  }

  buildAlternateElement({ i, u, n, f, r, t, s, h }) {
    const link = document.createElement("a");
    // Same structure as the server-rendered thumbnails, plus tw:hidden since
    // the carousel window is still on the first three thumbnails. Hover
    // selection is delegated to the track container (see the ERB partial)
    // and the variant URL is read from href, so neither is repeated here.
    link.className = "tw:block tw:w-full product-box-alternate tw:min-w-4 tw:hidden";
    link.href = u;
    link.dataset.thumbCarouselTarget = "image";
    link.dataset.productVariantId = i;
    link.dataset.productVariantName = n;
    link.dataset.productVariantFirstWord = f;
    link.dataset.productVariantRestOfName = r;
    // Image URLs may be absent from the payload (unpromoted Shrine derivative):
    // fall back to "" like the server-rendered thumbnails do, instead of
    // letting img.src = undefined trigger a request to /undefined.
    link.dataset.thumbnailSrc = t ?? "";
    if (h) link.dataset.hoverImgSrc = h;

    const img = document.createElement("img");
    img.className = "tw:object-contain";
    img.alt = "";
    img.src = s ?? "";
    link.appendChild(img);

    return link;
  }

  // delayedSelect/cancelSelect are bound on the carousel track with
  // mouseover/mouseout (which bubble, unlike mouseenter/mouseleave) so a
  // single data-action pair serves every thumbnail. Moves between descendants
  // of the same thumbnail are filtered out to keep the previous per-thumbnail
  // mouseenter/mouseleave semantics.
  delayedSelect(e) {
    this.materializeAlternates();
    const alternate = e.target.closest(".product-box-alternate");
    if (!alternate) return;
    if (e.relatedTarget && alternate.contains(e.relatedTarget)) return;
    this.hoverTimeout = setTimeout(() => {
      this.select(e)
    }, 300);
  }

  cancelSelect(e) {
    const alternate = e?.target?.closest?.(".product-box-alternate");
    if (alternate && e.relatedTarget && alternate.contains(e.relatedTarget)) return;
    clearTimeout(this.hoverTimeout)
    this.hoverTimeout = null
  }

  startCarousel() {
    this.materializeAlternates();
    this.stopCarousel();
    let next = this.nextVariantElement();
    if (next) {
      this.carouselTimer = setInterval(this.selectNext.bind(this), 2500);
      this.preload(next);
    }
  }

  stopCarousel() {
    if (this.carouselTimer) {
      clearInterval(this.carouselTimer);
      this.carouselTimer = null;
    }
  }

  select(event) {
    let clicked = event.target.closest(".product-box-alternate");
    if (!clicked) return;
    event.preventDefault();
    this.selectVariantElement(clicked);
  }

  selectNext() {
    this.materializeAlternates();
    this.selectVariantElement(this.nextVariantElement());
    this.preload(this.nextVariantElement());
  }

  selectPrevious() {
    this.materializeAlternates();
    this.selectVariantElement(this.previousVariantElement());
    this.preload(this.previousVariantElement())
  }

  nextVariantElement() {
    return (
      this.element.querySelector(".product-box-alternate.--selected ~ .product-box-alternate") ||
      this.element.querySelector(".product-box-alternate")
    );
  }

  previousVariantElement() {
    const variants = Array.from(this.element.querySelectorAll(".product-box-alternate"));
    const selectedIndex = variants.findIndex(el => el.classList.contains("--selected"));

    if (selectedIndex === -1) return null; // none selected
    if (selectedIndex > 0) return variants[selectedIndex - 1]; // previous exists

    return variants[variants.length - 1]; // wrap to last
  }

  selectVariantElement(clicked) {
    // The thumbnail's href IS the variant URL (no data duplicate).
    // The card is covered by two anchors (the image and the title, with the
    // carousel between them), so every one of them has to follow the selection.
    const variantUrl = this.variantUrlWithPageQuery(clicked.getAttribute("href"));
    this.linkTargets.forEach((link) => { link.href = variantUrl; });
    this.thumbnailTarget.src = clicked.dataset.thumbnailSrc;
    //this.productVariantNameTarget.textContent = clicked.dataset.productVariantName;

    this.productVariantFirstWordTarget.textContent = clicked.dataset.productVariantFirstWord;
    this.productVariantRestOfNameTarget.textContent = clicked.dataset.productVariantRestOfName;

    if (this.thumbnailHoverTarget) this.thumbnailHoverTarget.src = clicked.dataset.hoverImgSrc || "";
    if (!clicked.dataset.hoverImgSrc) {
      this.thumbnailTarget.classList.remove('--hidden-hover')
      this.thumbnailHoverTarget.classList.remove('--shown-hover')
      this.thumbnailHoverTarget.classList.add('d-none')
    } else {
      this.thumbnailTarget.classList.add('--hidden-hover')
      this.thumbnailHoverTarget.classList.add('--shown-hover')
      this.thumbnailHoverTarget.classList.remove('d-none')
    }

    if (this.hasFormTarget) {
      // The "form" target is the hidden add-to-cart button: its dataset is
      // both the GA4 payload and the POST body built by add_to_cart_controller.
      this.formTarget.dataset.itemId = clicked.dataset.productVariantId;
      this.formTarget.dataset.itemName = clicked.dataset.productVariantName;
      this.formTarget.dataset.itemImg = clicked.dataset.thumbnailSrc;
    }

    let previouslySelected = this.element.querySelector(".product-box-alternate.--selected");
    if (previouslySelected) previouslySelected.classList.remove("--selected");

    if (!clicked.classList.contains("--selected")) {
      clicked.classList.add("--selected");
    }
  }

  // The card's anchors are rendered with the catalogue's query string (search,
  // filters, order_by) because the product page's breadcrumb reads it to link
  // back to the results the visitor came from. The thumbnails' href carries no
  // query string, so merge the current one in when copying it over. Merging
  // rather than concatenating keeps any param the href already holds, and
  // leaves a fragment where it belongs.
  variantUrlWithPageQuery(href) {
    if (!href) return href;

    const url = new URL(href, window.location.origin);
    // Snapshot the href's own keys before appending: the catalogue's filters are
    // multi-valued (color_category_ids[]=1&color_category_ids[]=2), so testing
    // has() inside the loop would drop every value after the first.
    const ownKeys = new Set(url.searchParams.keys());
    new URLSearchParams(window.location.search).forEach((value, key) => {
      if (!ownKeys.has(key)) url.searchParams.append(key, value);
    });

    return `${url.pathname}${url.search}${url.hash}`;
  }

  preload(variantElement) {
    (new Image()).src = variantElement.dataset.thumbnailSrc;
  }
}
